import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/auth.store';
import { Button, Input } from '../../components/common';
import { useDocumentTitle } from '../../hooks';
import './Auth.css';

interface RegisterFormProps {
  onSuccess?: () => void;
}

/**
 * RegisterForm Component
 * Handles user registration with email, password, and name
 * Implements Requirements 1.1, 1.2
 */
export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, error, isLoading, clearError } = useAuthStore();
  
  // Set document title
  useDocumentTitle('pageTitles.register');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('validation.nameRequired');
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = t('validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('validation.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('validation.passwordTooShort');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear global error
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await register(formData);
      
      if (onSuccess) {
        onSuccess();
      } else {
        // Navigate to email verification page
        navigate('/verify-email', { state: { email: formData.email } });
      }
    } catch (err) {
      // Error is handled by the store
    }
  };

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <h2 className="auth-form__title">{t('auth.register')}</h2>
      
      {error && (
        <div className="auth-form__error" role="alert">
          {error}
        </div>
      )}

      <Input
        label={t('auth.name')}
        type="text"
        name="name"
        value={formData.name}
        onChange={handleChange}
        error={errors.name}
        placeholder={t('auth.enterName')}
        autoComplete="name"
        required
      />

      <Input
        label={t('auth.email')}
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        placeholder={t('auth.enterEmail')}
        autoComplete="email"
        required
      />

      <Input
        label={t('auth.password')}
        type="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        error={errors.password}
        placeholder={t('auth.enterPasswordMin')}
        autoComplete="new-password"
        required
      />

      <Button
        type="submit"
        variant="primary"
        loading={isLoading}
        disabled={isLoading}
        fullWidth
      >
        {isLoading ? t('auth.creatingAccount') : t('auth.register')}
      </Button>

      <p className="auth-form__footer">
        {t('auth.alreadyHaveAccount')}{' '}
        <a href="/login" className="auth-form__link">
          {t('auth.login')}
        </a>
      </p>
    </form>
  );
};
